import { useState, useEffect, useMemo } from 'react';
import { logoCandidates, logoFallbackColor } from '../lib/logo';

interface PortalLogoProps {
    /** The `logo` field from an `IdibuPortal` — filename, domain, or full URL. */
    logo?: string;
    /** Display name — used for the colored-initial fallback. */
    name: string;
    /** Box size in pixels. Defaults to 44. */
    size?: number;
    /** Inner image max size in pixels. Defaults to size minus 12 (room to breathe). */
    imageSize?: number;
    /** Border radius for the outer container. Defaults to var(--radius-md). */
    radius?: number | string;
    /** Background of the outer container when an image is showing. */
    background?: string;
    className?: string;
}

/**
 * Renders a portal logo with a multi-source fallback chain. On each `<img>`
 * error we advance to the next candidate (Clearbit → DuckDuckGo → Google
 * favicon). Once all are exhausted, we render a colored-initials box so
 * the layout never collapses or shows a broken-image icon.
 */
export default function PortalLogo({
    logo,
    name,
    size = 44,
    imageSize,
    radius = 'var(--radius-md)',
    background = 'var(--surface-tertiary)',
    className,
}: PortalLogoProps) {
    const candidates = useMemo(() => logoCandidates(logo), [logo]);
    const [index, setIndex] = useState(0);

    // Reset to first candidate when the underlying logo changes.
    useEffect(() => {
        setIndex(0);
    }, [logo]);

    const exhausted = index >= candidates.length;
    const src = exhausted ? null : candidates[index];

    const wrapStyle: React.CSSProperties = {
        display: 'grid',
        width: size,
        height: size,
        flexShrink: 0,
        placeItems: 'center',
        borderRadius: typeof radius === 'number' ? `${radius}px` : radius,
        background: exhausted ? logoFallbackColor(name) : background,
        overflow: 'hidden',
    };

    if (exhausted) {
        const initials = (name || '?').slice(0, 2).toUpperCase();
        return (
            <span className={className} style={wrapStyle} aria-hidden>
                <span
                    style={{
                        color: '#fff',
                        fontFamily: 'var(--v2-font-sans)',
                        fontSize: Math.max(11, Math.round(size * 0.32)),
                        fontWeight: 600,
                        letterSpacing: '0.02em',
                    }}
                >
                    {initials}
                </span>
            </span>
        );
    }

    const innerSize = imageSize ?? Math.max(16, size - 12);

    return (
        <span className={className} style={wrapStyle} aria-hidden>
            <img
                key={src ?? ''}
                src={src ?? ''}
                alt=""
                loading="lazy"
                referrerPolicy="no-referrer"
                onError={() => setIndex((i) => i + 1)}
                style={{
                    maxWidth: innerSize,
                    maxHeight: innerSize,
                    objectFit: 'contain',
                }}
            />
        </span>
    );
}
